import React, {useEffect, useState} from "react";
import {
    Table,
    Card,
    Typography,
    message,
    DatePicker,
    Input,
    Row,
    Col,
} from "antd";
import {SearchOutlined} from "@ant-design/icons";
import api from "../../api";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";

const {Title} = Typography;
const {RangePicker} = DatePicker;

const CCTripsPage: React.FC = () => {
    const [trips, setTrips] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchTrips(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, dateRange, searchText]);

    useEffect(() => {
        // Reset to page 1 on any filter change
        setPagination((prev) => ({
            ...prev,
            current: 1,
        }));
    }, [searchText, dateRange]);

    const fetchTrips = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params: any = {
                page,
                page_size: pageSize,
            };

            if (dateRange && dateRange[0] && dateRange[1]) {
                params.checkin_after = dateRange[0].startOf("day").toISOString();
                params.checkin_before = dateRange[1].endOf("day").toISOString();
            }

            if (searchText) {
                params.search = searchText.trim();
            }

            const res = await api.get("/cc/trips/", {params});

            setTrips(res.data.results); // paginated results
            setPagination((prev) => ({
                ...prev,
                total: res.data.count,
            }));
        } catch {
            message.error("Failed to load trips");
        } finally {
            setLoading(false);
        }
    };

    const toNum = (v: unknown) => (v == null || v === "" ? 0 : Number(v));

    const columns = [
        {title: "Customer Name", dataIndex: "customer_name"},
        {title: "MMID", dataIndex: "mmid"},

        {
            title: "Check-in Date",
            render: (_: any, rec: any) =>
                rec?.hotel?.checkin_date ?? rec?.checkin_date ?? "-",
        },
        {
            title: "Check-out Date",
            render: (_: any, rec: any) =>
                rec?.hotel?.checkout_date ?? rec?.checkout_date ?? "-",
        },

        {
            title: "IN Departure Date",
            render: (_: any, rec: any) =>
                rec?.flight?.in_departure_date ?? rec?.in_departure_date ?? "-",
        },
        {
            title: "IN Flight Number",
            render: (_: any, rec: any) =>
                rec?.flight?.in_flight_number ?? rec?.in_flight_number ?? "-",
        },
        {
            title: "IN Arrival Airport",
            render: (_: any, rec: any) =>
                rec?.flight?.in_arrival_airport ?? rec?.in_arrival_airport ?? "-",
        },
        {
            title: "IN Arrival Time",
            render: (_: any, rec: any) => {
                const time =
                    rec?.flight?.in_arrival_time ?? rec?.in_arrival_time ?? null;
                return time ? dayjs(time, ["HH:mm:ss", "HH:mm"]).format("HH:mm") : "-";
            },
        },

        // If you want to show buy-in on this page too:
        {
            title: "Buy-in Method",
            render: (_: any, rec: any) =>
                rec?.trip?.buy_in_method ?? rec?.buy_in_method ?? "-",
        },
        {
            title: "Buy-in Amount",
            render: (_: any, rec: any) => {
                const amount = rec?.trip?.buyin_amount ?? rec?.buyin_amount ?? null;
                if (amount == null) return "-";
                const currency =
                    rec?.trip?.buyin_currency ?? rec?.buyin_currency ?? "";
                const symbolMap: Record<string, string> = {
                    KRW: "₩", Yen: "¥", HKD: "HK$", NTD: "NT$", USDT: "US$",
                };
                const symbol = symbolMap[currency] ?? "";
                return `${symbol}${Number(amount).toLocaleString()}`;
            },
        },
        {
            title: "Total Flight Cost (KRW)",
            render: (_: any, rec: any) => {
                const v =
                    rec?.flight?.total_flight_cost_krw ?? rec?.total_flight_cost_krw ?? 0;
                return `₩${toNum(v).toLocaleString()}`;
            },
        },
    ];

    return (
        <Card
            className="rounded-2xl shadow-sm"
            title={
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={3} style={{margin: 0}}>
                            Casino Club Trips
                        </Title>
                    </Col>
                </Row>
            }
        >
            <Row gutter={[16, 16]} style={{marginBottom: 16}} wrap>
                <Col xs={24} sm={16} md={12} lg={8}>
                    <Input
                        placeholder="Search by Customer or MMID"
                        prefix={<SearchOutlined/>}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        className="rounded-md"
                    />
                </Col>
                <Col xs={24} sm={16} md={12} lg={8}>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                        style={{width: "100%"}}
                        className="rounded-md"
                        placeholder={["Check-in Start", "Check-out End"]}
                    />
                </Col>
            </Row>

            <Table
                rowKey={(r) => r.trip_id ?? r.id}
                loading={loading}
                dataSource={trips}
                columns={columns}
                scroll={{x: true}}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    onChange: (page, pageSize) =>
                        setPagination((prev) => ({...prev, current: page, pageSize})),
                }}
                onRow={(record) => ({
                    onClick: () =>
                        navigate(`/cc/trips/${(record.trip_id ?? record.id)}/details/`),
                    style: {
                        cursor: "pointer",
                        transition: "background-color 0.2s ease",
                        backgroundColor:
                            (record?.hotel?.checkout_date ?? record?.checkout_date) &&
                            dayjs(record?.hotel?.checkout_date ?? record?.checkout_date).isBefore(
                                dayjs(),
                                "day"
                            )
                                ? "#d9d9d9"
                                : "#ffffff",
                    },
                })}
                className="rounded-xl overflow-hidden"
            />
        </Card>
    );
};

export default CCTripsPage;